import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ui/components/ui/dialog';
import { Button } from '@ui/components/ui/button';
import { SprintProductEntity } from '@data';

/**
 * What happened to the remark, said once, when the client comes back to a report that has been answered.
 *
 * The report itself carries the answer beside the summary and keeps carrying it, so this dialog is not
 * where the information lives — it is only how the client finds out without having to notice a box that
 * changed colour. Before it, dismissing was silent: the remark disappeared, the buttons came back, and
 * nothing anywhere said we had looked.
 *
 * Two ways out, because those are the only two answers left to give — and they are the same two controls
 * the report offers behind it, wired to the same dialogs rather than to a second copy of them.
 */
export const DisputeAnsweredDialog = ({
  sprintProduct,
  answeredBy,
  onApprove,
  onDisputeAgain,
  onClose,
}: {
  sprintProduct: SprintProductEntity;
  answeredBy: string;
  onApprove: () => void;
  onDisputeAgain: () => void;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const resolution = sprintProduct.disputeResolution ?? 'dismissed';

  return (
    <Dialog open onOpenChange={next => !next && onClose()}>
      <DialogContent className="gap-6 p-8 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`disputeAnsweredTitle.${resolution}`)}</DialogTitle>
          <DialogDescription>{t(`disputeAnswer.${resolution}`)}</DialogDescription>
        </DialogHeader>

        {/* The reason, when one was given. Quoted in the same box as the client's own remark is on the
            report, so the two read as the exchange they are. */}
        {sprintProduct.disputeResolutionNote && (
          <div className="rounded-lg bg-muted p-3">
            <p className="whitespace-pre-line text-sm text-foreground">{sprintProduct.disputeResolutionNote}</p>
            {answeredBy && (
              <p className="mt-2 text-xs text-muted-foreground">{t('remarkAnsweredBy', { by: answeredBy })}</p>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground">{t('disputeAnsweredNext')}</p>

        <DialogFooter className="-mx-8 -mb-8 mt-2 p-2.5">
          <Button variant="outline" onClick={onDisputeAgain}>
            {t('actions.dispute')}
          </Button>
          <Button onClick={onApprove}>{t('actions.approve')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisputeAnsweredDialog;
